import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import ActionIconButton from '@components/buttons/ActionIconButton';
import BasicCard from '@components/cards/BasicCard';
import ProviderForm from '@components/forms/ProviderForm';
import Modal, { ModalElement } from '@components/Modal';
import ProviderTracker from '@components/ProviderTracker';
import Icon from '@components/text/Icon';
import Tracker from '@components/trackers/Tracker';
import styles from '@components/trackers/Tracker.module.css';
import TrackerPopover from '@components/trackers/TrackerPopover';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import useDestroyProvider from '@hooks/mutations/userDestroyProvider';
import useLazyQueryWithPromise from '@hooks/useLazyQueryWithPromise';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithCareProfileQuery,
  GetJourneyWithDocumentsQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  PdfTypeEnum,
  ProviderFragment,
  TrackerStatusEnum
} from '@typing/Generated';
import { downloadFile } from '@utils/downloadUtils';
import { providersBatched, sortByTimestamp } from '@utils/modelUtils';

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'] | GetJourneyWithDocumentsQuery['journey'];
};

type ProviderProps = {
  handleDestroy: (p: ProviderFragment) => void;
  handleEdit: (p: ProviderFragment) => void;
  provider: ProviderFragment;
};

type ProviderDescriptionProps = {
  description: string | null;
  labelKey: string;
  visible: boolean;
};

const ProviderDescription = ({ description, labelKey, visible }: ProviderDescriptionProps) => {
  if (!visible) return null;

  return (
    <div className={styles.description}>
      <div className={styles.noWrap}>
        <BodyCopyBold color="primary700">
          <FormattedMessage id={labelKey} />
        </BodyCopyBold>
      </div>

      <BodyCopyBold color="neutral500">{description}</BodyCopyBold>
    </div>
  );
};

const Provider = ({ handleDestroy, handleEdit, provider }: ProviderProps) => {
  const opener = useOpener();

  const displayDetailsToggle = useMemo(() => {
    if (
      provider.address ||
      provider.fax ||
      provider.notes ||
      provider.patientPortal ||
      provider.phone ||
      provider.practiceName ||
      provider.website
    ) {
      return true;
    }
    return false;
  }, [provider]);

  return (
    <BasicCard boxShadow hover="none" padding="none">
      <div data-test="provider">
        <div className={styles.item}>
          <div className={styles.titleAndIcon}>
            <Icon color="primary" icon="userDoctor" />

            <BodyCopyBold color="primary700">{provider.name}</BodyCopyBold>
          </div>
          <div className={styles.actions}>
            {displayDetailsToggle && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.toggle"
                data-test="details"
                icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
                onClick={opener.toggle}
              />
            )}

            <TrackerPopover handleDestroy={() => handleDestroy(provider)} handleEdit={() => handleEdit(provider)} />
          </div>
        </div>
        <ExpandableSection isOpen={opener.isOpen} key={`providerDescriptions${provider.id}`}>
          <div className={styles.descriptions}>
            <ProviderDescription
              description={provider.address}
              labelKey="pages.client.providers.list.address"
              visible={!!provider.address}
            />
            <ProviderDescription
              description={provider.phone}
              labelKey="pages.client.providers.list.phone"
              visible={!!provider.phone}
            />
            <ProviderDescription
              description={provider.fax}
              labelKey="pages.client.providers.list.fax"
              visible={!!provider.fax}
            />
            <ProviderDescription
              description={provider.website}
              labelKey="pages.client.providers.list.website"
              visible={!!provider.website}
            />
            <ProviderDescription
              description={provider.patientPortal}
              labelKey="pages.client.providers.list.patientPortal"
              visible={!!provider.patientPortal}
            />
            <ProviderDescription
              description={provider.practiceName}
              labelKey="pages.client.providers.list.practiceName"
              visible={!!provider.practiceName}
            />
            <ProviderDescription
              description={provider.notes}
              labelKey="pages.client.providers.list.notes"
              visible={!!provider.notes}
            />
          </div>
        </ExpandableSection>
      </div>
    </BasicCard>
  );
};

const Providers = ({ journey }: Props) => {
  const intl = useIntl();
  const trackerOpener = useOpener();
  const addOpener = useOpener();
  const providerModalRef = useRef<ModalElement>(null);
  const [editingProvider, setEditingProvider] = useState<ProviderFragment | undefined>(undefined);
  const batchedProviders = useMemo(() => providersBatched(journey.providers), [journey.providers]);

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>) => {
      event.preventDefault();
      getPdfDownload({
        fetchPolicy: 'no-cache',
        variables: { journeyId: journey.id, type: PdfTypeEnum.provider_tracker }
      }).then(async response => {
        downloadFile(response.data.pdfDownload.url, 'grayce-providers.pdf');
      });
    },
    [getPdfDownload, journey.id]
  );

  const handleProviderError = () => {
    if (providerModalRef.current) {
      providerModalRef.current.scrollToTop();
    }
  };

  const destroyProvider = useDestroyProvider(journey.id);

  return (
    <div className={styles.trackerContainer}>
      <Tracker
        canDownload={journey.providers.length > 0}
        handleDownload={handleDownload}
        kind="provider"
        opener={journey.providerTrackerStatus === TrackerStatusEnum.COMPLETED ? addOpener : trackerOpener}
        trackerStatus={journey.providerTrackerStatus}
      >
        <div className={styles.items}>
          {sortByTimestamp(journey.providers, provider => provider.createdAt).map(provider => (
            <Provider
              handleDestroy={p => {
                destroyProvider({
                  id: p.id
                });
              }}
              handleEdit={p => {
                setEditingProvider(p);
                addOpener.open();
              }}
              key={provider.id}
              provider={provider}
            />
          ))}
        </div>
      </Tracker>
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={trackerOpener}
        restartWizard
      />
      <Modal
        opener={addOpener}
        ref={providerModalRef}
        title={
          editingProvider
            ? intl.formatMessage({ id: 'models.careProfile.updateProvider' })
            : intl.formatMessage({ id: 'models.careProfile.addNewProvider' })
        }
      >
        {({ modalDisplayed }) => (
          <ProviderForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            kindEditable={!editingProvider}
            onCancel={addOpener.close}
            onError={handleProviderError}
            onSuccess={addOpener.close}
            provider={editingProvider}
          />
        )}
      </Modal>
    </div>
  );
};

export default Providers;
